import { Button, Icon, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "components/Forms";
import { useQueryClient } from "react-query";
import {
  organizationQuestionnaireKeys
} from "../../../../../../hooks/query";
import { useUpdateBusinessTravelCategory } from "../../../../../../hooks/query/scope-3";

import CategoryForm from "./CategoryForm";
import * as React from "react";
import { useCallback, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { BusinessTravelModalWrapper } from "../CategoryModalWrappers";
import {
  businessTravelKeys,
  useAddBusinessTravelItems,
  useDeleteBusinessTravelItem,
  useDownloadBusinessTravelTemplate,
  useGetBusinessTravelItems,
  useImportBusinessTravelItems,
  useUpdateBusinessTravelItem
} from "../../../../../../hooks/query/questionnaire/categories/business-travel";
import { FaFileImport } from "react-icons/fa";
import { targetForCategory } from "../../../../aiIngestTarget";
import { buildDimensionFormDefaults, withDimensionItems } from "./helpers";

function CategoryBusinessTravel({
                                  defaultValues,
                                  tabKey,
                                  handleCategoryChange,
                                  finalize,
                                  finalizeQuestionnaire,
                                  isPreview,
                                  organizationStructure,
                                  dimensionItems
                                }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { id: questionnaireId } = defaultValues;
  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [t(`inputs.${tabKey}.modeOfTransport`), t(`inputs.${tabKey}.transportSubtype`), t(`inputs.${tabKey}.distanceTravelled`)];
  const attributesOrder = ["modeOfTransport", "transportSubtype", "distanceTravelled"];
  const FORM_INPUT_KEY = "businessTravel";

  const {
    handleSubmit,
    formState: { isDirty }
  } = useForm();

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Business Travel saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateBusinessTravelCategory(queryConfig);

  const formId = `formStep${tabKey}`;

  const onSubmit = (() => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {};
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const prepareSectionValues = useCallback(items => {
    const modeOfTransportKey = "organizationQuestionnaire.scope3.inputs.modeOfTransportOptions";
    const transportSubtypeKey = "organizationQuestionnaire.scope3.inputs.transportSubtypeOptions";

    return items.map(item => {
      return {
        id: item.id ?? null,
        dimensionItems: item.dimensionItems ?? null,
        dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
        modeOfTransportValue: item.modeOfTransport,
        modeOfTransport: i18n.exists(`${modeOfTransportKey}.${item.modeOfTransport}`) ?
          t(`inputs.modeOfTransportOptions.${item.modeOfTransport}`) : item.modeOfTransport ?? "",
        transportSubtypeValue: item.transportSubtype,
        transportSubtype: i18n.exists(`${transportSubtypeKey}.${item.modeOfTransport}.${item.transportSubtype}`) ?
          t(`inputs.transportSubtypeOptions.${item.modeOfTransport}.${item.transportSubtype}`) : item.transportSubtype ?? "",
        distanceTravelled: item.distanceTravelled ?? ""
      };
    });
  }, [i18n, t]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        modeOfTransport: item.modeOfTransportValue,
        transportSubtype: item.transportSubtypeValue,
        distanceTravelled: item.distanceTravelled
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddBusinessTravelItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(businessTravelKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateBusinessTravelItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(businessTravelKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteBusinessTravelItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(businessTravelKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadBusinessTravelTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportBusinessTravelItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(businessTravelKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({ ...el }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      modeOfTransport: "",
      transportSubtype: "",
      distanceTravelled: "",
      ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetBusinessTravelItems}
        isPreview={isPreview}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-${tabKey}-btn`}
        aiTarget={targetForCategory(tabKey)}
        totalsConfig={{ valueKey: "distanceTravelled" }}
      />

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <BusinessTravelModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
          />
        )
      }
    </CategoryForm>
  );
}

export default CategoryBusinessTravel;

export const ImportBusinessTravelItemsBtn = ({ questionnaireId }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation("translation");
  const importBtnId = "import-business-travel-items-btn";

  const importBtnWidth = useBreakpointValue({
    base: "28",
    md: "32"
  });
  const btnFontSize = useBreakpointValue({
    base: "sm",
    md: "md"
  });
  const containerDisplay = useBreakpointValue({
    base: "block",
    md: "flex"
  });

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(businessTravelKeys.items());
    },
    onError: err => {
      let errorMessage = "";
      if (err.response?.data?.errorCode === "file-argument-not-valid") {
        errorMessage = err.response?.data?.parameters?.message ?? err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response?.data?.message ?? err.message;
      } else {
        errorMessage = err.message;
      }
      showErrorToast(toast, errorMessage);
    },
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: importData } = useImportBusinessTravelItems(queryConfig);

  const handleDataImport = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      dispatch(showAppSpinner());
      document.getElementById(importBtnId).value = null;
      importData({
        questionnaireId: questionnaireId,
        data: formData
      });
    }
  };

  const handleImportDataClick = () => {
    const importBtn = document.getElementById(importBtnId);
    if (importBtn) {
      importBtn.click();
    }
  };

  return (
    <>
      <FormInput
        id={importBtnId}
        name={importBtnId}
        type="file"
        onChange={(e) => handleDataImport(e)}
        paddingInlineStart="0"
        border="none"
        accept={".xlsx, .xls"}
        controlProps={{ display: "none" }}
      />
      <Button
        colorScheme="white"
        color="esg_lightgreen"
        border={"1px solid"}
        borderColor={"esg_lightgreen"}
        width={importBtnWidth}
        fontSize={btnFontSize}
        onClick={handleImportDataClick}
        marginRight={5}
      >
        <Icon
          as={FaFileImport}
          inlineSize="3"
          blockSize="3"
          color="esg_green"
          marginEnd="2"
          marginTop={containerDisplay === "block" ? "0" : "1"}
        />
        {t("organizationQuestionnaire.scope1.inputs.importData")}
      </Button>
    </>
  );
};

